import {
  inject,
  computed,
  Injector,
  Component,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { FormsService } from 'src/app/core/services/forms.service';

import {
  SuiDialogConfig,
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { FormProperty } from 'src/app/core/models/form.models';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { AlgorithmScheduleComponent } from '../algorithm-schedule/algorithm-schedule.component';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData {
  algorithmId: number;
  title: string;
  formProperties: FormProperty[];
  canSchedule?: boolean;
  initialSchedule?: boolean;
  initialValues?: Record<string, any>;
  editMode?: boolean;
}
interface DialogOutput {}

@Component({
  selector: 'app-alghorithm-dialog',
  templateUrl: './alghorithm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    AlgorithmScheduleComponent,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class AlghorithmDialogComponent extends SimpleDialogBase<
  DialogData,
  DialogOutput
> {
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '20vw',
    minWidth: 400,
    maxWidth: 800,
  };
  private formService = inject(FormsService);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);

  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  objectIcon = faKey;

  get algorithmId() {
    return this.dialogData.algorithmId;
  }
  get title() {
    return this.dialogData.title;
  }
  get formProperties() {
    return this.dialogData.formProperties;
  }
  get canSchedule() {
    return !!this.dialogData.canSchedule;
  }
  get initialSchedule() {
    return !!this.dialogData.initialSchedule;
  }
  get initialValues(): Record<string, any> | undefined {
    return this.dialogData.initialValues;
  }
  get editMode() {
    return !!this.dialogData.editMode;
  }
  get showSchedule() {
    return this.canSchedule || this.editMode;
  }
  get initialIntervalMinutes(): number {
    const value = this.dialogData.initialValues?.['schedule_interval_minutes'];
    return typeof value === 'number' && value > 0 ? value : 60;
  }
  get initialRecurrence() {
    return this.dialogData.initialValues?.['schedule_recurrence'] ?? null;
  }
  get initialTimezoneId(): number | null {
    const value = this.dialogData.initialValues?.['schedule_timezone_id'];
    return typeof value === 'number' ? value : null;
  }

  formGroup!: FormGroup;

  private formValueChanges!: ReturnType<typeof toSignal<any>>;
  private formStatusChanges!: ReturnType<typeof toSignal<any>>;

  public isDirty = computed(() => {
    const signalValue = this.formValueChanges?.();
    // ! since formGroup.dirty is broken (always false), track dirty via signal
    // ! if signal has emitted any value, form has changed
    // ! some day we shall drop this <app-dynamic-form> and all will be fine
    const isDirty = signalValue !== undefined;

    return isDirty;
  });

  public isValid = computed(() => {
    const status = this.formStatusChanges?.();
    return status === 'VALID';
  });

  public isScheduleMode = computed(() => {
    this.formValueChanges?.();
    return !!this.formGroup?.get('schedule')?.value;
  });

  public saveButtonText = computed(() => {
    if (this.editMode) {
      return this.translateService.instant('btn.save');
    }
    if (this.canSchedule && this.isScheduleMode()) {
      return this.translateService.instant('btn.schedule');
    }
    if (this.canSchedule) {
      return this.translateService.instant('btn.run-now');
    }
    return this.translateService.instant('btn.create');
  });

  public saveButtonIcon = computed<IconProp | undefined>(() => {
    if (this.editMode) return undefined;
    if (!this.canSchedule) return undefined;
    return this.isScheduleMode() ? ['fas', 'clock'] : ['fas', 'play'];
  });

  ngOnInit() {
    const hasFormProps = this.formProperties && this.formProperties.length > 0;

    if (hasFormProps) {
      this.formGroup = this.formService.createFormGroup(this.formProperties);
    } else if (this.showSchedule) {
      // schedule sub-component will register its own controls into this group
      this.formGroup = new FormGroup({});
    }

    if (this.formGroup && this.initialValues) {
      this.formGroup.patchValue(this.initialValues, { emitEvent: false });
    }

    if (this.formGroup) {
      this.formValueChanges = toSignal(this.formGroup.valueChanges, {
        injector: this.injector,
        requireSync: false,
      });
      this.formStatusChanges = toSignal(this.formGroup.statusChanges, {
        injector: this.injector,
        initialValue: this.formGroup.status,
      });
    }
  }

  ngAfterViewInit() {
    if (!this.formGroup) return;

    this.formGroup.valueChanges.subscribe((v) => {});
    this.formGroup.statusChanges.subscribe((s) => {});

    this.setupDirtyHandler(this.isDirty, this.isValid);

    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    return {
      reason: DialogCloseReason.Save,
      data: this.formGroup.value,
    };
  }

  save(): void {
    if (!this.isValid()) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
